import type {
  CreateEventRequest,
  CreateSessionEventRequest,
} from "@/shared/domain/events/EventRecord";

import type { ClientToolDeps, ClientToolExecutionResult } from "./ClientTools";

/**
 * The input fields both event tools take. `client_create_events` adds the
 * associations a dataset event is about; `client_create_session_events` adds
 * the session and topics.
 */
export interface EventInputCommon {
  name: string;
  description?: string;
  start_time: number;
  end_time: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
  display_options?: {
    color?: string;
  };
}

/** The input schema of the fields in {@link EventInputCommon}. */
export const EVENT_COMMON_PROPERTIES: Record<string, unknown> = {
  name: {
    type: "string",
    description:
      "Brief name for the event (e.g., 'CPU Spike', 'Takeoff', 'Crash').",
  },
  description: {
    type: "string",
    description: "Optional human-readable description of the event.",
  },
  start_time: {
    type: "integer",
    description: "Start time in nanoseconds since Unix epoch.",
  },
  end_time: {
    type: "integer",
    description:
      "End time in nanoseconds since Unix epoch. Equal to start_time for instantaneous events.",
  },
  tags: {
    type: "array",
    items: { type: "string" },
    description: "Optional tags for categorization.",
  },
  metadata: {
    type: "object",
    description: "Optional key-value metadata for discovery.",
  },
  display_options: {
    type: "object",
    properties: {
      color: {
        type: "string",
        description: "CSS color for visualization (hex, rgb, or named color).",
      },
    },
  },
};

/** The request fields built from {@link EventInputCommon}. */
export function commonEventFields(event: EventInputCommon) {
  return {
    name: event.name,
    description: event.description,
    start_time: BigInt(event.start_time),
    end_time: BigInt(event.end_time),
    tags: event.tags ?? [],
    metadata: event.metadata ?? {},
    display_options: event.display_options,
  };
}

/**
 * Create each of `events` in the org, one request each, and report every
 * event's ID or error. `toRequest` builds an event's request, throwing when
 * the event cannot be created; a refused event does not stop the others.
 * Succeeds only when every event was created.
 */
export async function createEachEvent<E extends EventInputCommon>(
  events: E[] | undefined,
  deps: ClientToolDeps | undefined,
  toRequest: (
    event: E,
    deps: ClientToolDeps,
  ) => CreateEventRequest | CreateSessionEventRequest,
): Promise<ClientToolExecutionResult> {
  if (!deps?.eventService || !deps?.orgId) {
    return {
      response: { error: "Event service or org ID not available" },
      success: false,
    };
  }

  if (!Array.isArray(events) || events.length === 0) {
    return {
      response: { error: "events array is required and must not be empty" },
      success: false,
    };
  }

  const results: Array<{
    name: string;
    event_id?: string;
    error?: string;
  }> = [];

  for (const event of events) {
    try {
      const created = await deps.eventService.createEvent(
        deps.orgId,
        toRequest(event, deps),
      );
      results.push({ name: event.name, event_id: created.event_id });
    } catch (err) {
      results.push({
        name: event.name,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const anySucceeded = results.some((r) => r.event_id !== undefined);
  const allSucceeded = results.every((r) => r.event_id !== undefined);

  if (anySucceeded) {
    deps.onEventCreated?.();
  }

  return {
    response: {
      created_events: results,
      total: results.length,
      succeeded: results.filter((r) => r.event_id !== undefined).length,
    },
    success: allSucceeded,
  };
}
